import { useState, useCallback, Fragment } from "react";
import {
	CheckboxFromProps,
	CheckboxGroupFromProps,
} from "./CheckboxProps";
import { Checkbox, FormHelperText, Tooltip } from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import { Controller, useFormContext } from "react-hook-form";
import { IsNullOrUndefined } from "utilities/utilities";

export const CheckboxFormItem = (props: CheckboxFromProps) => {
	const {
		label,
		name,
		disabled,
		required,
		styleCheckBox,
	} = props;

	const {
		control,
		setValue,
		watch,
	} = useFormContext();

	return (
		<Controller
			name={name}
			control={control}
			render={({
				field: { onChange, onBlur, value, ref },
				fieldState: { invalid, error },
			}) => {
				return (
					<>
						<div style={{ display: "inline-flex" }}>
							<FormControlLabel
								control={
									<Checkbox
										ref={ref}
										id={name}
										data-testid={name}
										disabled={disabled ?? false}
										checked={watch(name) ?? false}
										onChange={(e) => {
											setValue(
												name,
												e.target.checked ? true : false
											);
											onChange(
												e.target.checked ? true : false
											);
											if (props.onChange) {
												props.onChange(e.target.checked ? true : false);
											}

										}}
										onBlur={onBlur}
										required={required}
										style={styleCheckBox}
									/>
								}
								style={styleCheckBox}
								label={label}
							/>
						</div>
						{error && (
							<FormHelperText
								sx={{
									color: "red !important",
									marginLeft: "-0.1px ",
								}}
							>
								{error.message}
							</FormHelperText>
						) }
					</>
				);
			}}
		/>
	);
};
export default CheckboxFormItem;

export const CheckboxGroupFormItem = (props: CheckboxGroupFromProps) => {
	const {
		label,
		name,
		required,
		disabled,
		options = [],
		row = true,
		style,
		styleCheckbox,
		styleLabel,
		onChange,
		isTooltip = true,
	} = props;

	const {
		control,
		setValue,
		clearErrors,
		watch,
	} = useFormContext();

	const [valueAll, setValueAll] = useState(false);

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.checked) {
			let fromValue = watch(name) ?? [];
			let values_ = options.filter(
				(f) => f.value === event.target.defaultValue
			)[0];
			if (values_) {
				fromValue.push(values_.value);
				setValue(name, fromValue);
				if (onChange) {
					onChange(event, fromValue);
				}
			}
		} else {
			let values_ = (watch(name) ?? [])
				.filter((f) => f !== event.target.defaultValue)
				.map((m) => m);
			setValue(name, values_);
			if (onChange) {
				onChange(event, values_);
			}
		}
		clearErrors(name);
	};

	const handleChangeAll = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		let values_ = options
			.filter((f) => f.disabled !== true)
			.map((m) => m.value);
		if (event.target.checked === false) {
			values_ = [];
		}
		setValueAll(event.target.checked);
		setValue(name, values_);
		clearErrors(name);
		if (onChange) {
			onChange(event, values_);
		}
	};

	const CheckboxGroup = useCallback((error) => {
		let watchValue = watch(name) ?? [];
		let colorErrorText = "rgba(0, 0, 0, 0.6)";
		let colorErrorCheck = "";
		if(error?.message != null)
		{
			colorErrorText = "#red !important";
			colorErrorCheck = "red !important";
		}
		
		let values_ = options.filter((f) => f.disabled !== true).map((m) => m.value);
		setValueAll(false);
		if (values_.length === watchValue.length) {
			setValueAll(true);
		}
		const handleCheck = (value)=>{
			return(
				watchValue.length > 0 ? 
				(watchValue).filter(
					(f) =>
						f ===
						value
				).length > 0
				: false
			)
		}
		return (
			<>
				{options.map((m) => (
					<Fragment key={m.value}>
						{!IsNullOrUndefined(m.sFullName) ? (
							<Tooltip title={isTooltip ? m.sFullName : ""}>
								<FormControlLabel
									key={"ChG_" + m.index}
									control={
										<Checkbox
											size="small"
											onChange={handleChange}
											value={m.value}
											data-testid={m.value}
											checked={handleCheck(m.value)}
											disabled={
												m.disabled ?? disabled
											}
											id={props.id}
											name={name}
											style={{
												color: colorErrorCheck,
												...styleCheckbox,
											}}
										/>
									}
									style={{
										color: colorErrorText,
										fontFamily: "Helvetica",
										fontSize: "1em",
										...styleLabel,
									}}
									label={m.label}
								/>
							</Tooltip>
						) : (
							<Tooltip placement="right" title={isTooltip ? m.label : ""}>
								<FormControlLabel
									key={"ChG_" + m.index}
									control={
										<Checkbox
											size="small"
											onChange={handleChange}
											value={m.value}
											data-testid={m.value}
											checked={handleCheck(m.value)}
											disabled={
												m.disabled ?? disabled
											}
											name={name}
											style={{
												color: colorErrorCheck,
												...styleCheckbox,
											}}
										/>
									}
									style={{
										color: colorErrorText,
										fontFamily: "Helvetica",
										fontSize: "1em",
										...styleLabel,
									}}
									label={
										<span
											style={{
												display: "-webkit-box ",
												WebkitBoxOrient:
													"vertical",
												WebkitLineClamp: "1 ",
												overflow: "hidden ",
												wordBreak: "break-word",
												textOverflow:
													"ellipsis",
											}}
										>
											{m.label}
										</span>
									}
								/>
							</Tooltip>
						)}
					</Fragment>
				))}
			</>
		);
	},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[options, watch, name, isTooltip, disabled, props.id, styleCheckbox, styleLabel]
	);

	return (
		<Controller
			name={name}
			control={control}
			render={({
				field: { onChange, onBlur, value, ref },
				fieldState: { invalid, error },
			}) => {
				return (
					<FormControl
						key={name}
						error={error?.message != null}
						size={"small"}
						sx={{ width: "100%" }}
						style={{ ...style }}
					>
						{label && (
							<FormLabel
								component="legend"
								sx={{
									color:
										error?.message != null
											? "red !important"
											: "",
								}}
							>
								{label}{" "}
								<span
									style={{
										color: "#FF0000",
										fontSize: "15px",
										marginLeft: "-4px",
									}}
								>
									{required === true ? " *" : ""}
								</span>
							</FormLabel>
						)}
						{props.AllowCheckAll && (
							<FormControlLabel
								style={{
									color:
										error?.message != null
											? "red !important"
											: "rgba(0, 0, 0, 0.6)",
									fontSize: "15px",
									paddingLeft: "0",
									display: "nline-flex !important",
								}}
								control={
									<Checkbox
										onChange={handleChangeAll}
										value={"All"}
										size="small"
										data-testid={"valueAll"}
										checked={valueAll}
										style={{
											color:
												error?.message != null
													? "red !important"
													: "",
										}}
										disabled={disabled}
									/>
								}
								label={
									props.labelCheckAll ??
									"เลือกทั้งหมด"
								}
							/>
						)}
						<FormGroup
							row={true}
							style={{
								marginLeft: "0px",
								flexDirection: row ? "row" : "column",
							}}
						>
							<CheckboxGroup error={error} />
						</FormGroup>
						{error && (
							<FormHelperText
								sx={{
									color: "red !important",
									marginLeft: "-0.1px ",
								}}
							>
								{error.message}
							</FormHelperText>
						)}
					</FormControl>
				);
			}}
		/>
	);
};